var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { map } from 'lodash';
import { getDOMNode } from 'libs/dom';
import { diff } from 'libs/react';
import { keyed } from 'libs/react';
import { useEffect } from 'libs/react';
import { useState } from 'libs/react';
import { useValue } from 'libs/react';
import { wait } from 'libs/util/time';
import { UnexpectedError } from 'libs/error/UnexpectedError';
import { React } from 'libs/react';
import './Transition.scss';
/**
 * @component Transition
 * @since 1.0.0
 */
export function Transition(props) {
    /*
      Here's how this thing work. The content state variable represent the
      actual content that is mounted on the screen. The content property
      however represent everything that is being animated or about to be
      animated. On each render, the diff occurs with this content.
  
      Then the inserted items goes into the present property and the removed
      items into the dismiss property. As soon as a present or dismiss
      animation start, the key is removed from the present or dismiss map.
      These maps represent the element to transition on the next frame.
     */
    const { appear, children, sequent, enqueue, presentClassName, dismissClassName, presentDuration, dismissDuration, presentDelay, dismissDelay, presentBeginHandler, dismissBeginHandler, presentFinishHandler, dismissFinishHandler } = Object.assign({ appear: true, sequent: true, enqueue: true, presentClassName: 'present', dismissClassName: 'dismiss', presentDuration: 750, dismissDuration: 750 }, props);
    const self = useValue(() => ({
        frame: 0,
        nodes: {},
        watch: {},
        empty: true,
        appear: true,
        animate: false,
        sequent: true,
        enqueue: true,
        content: {},
        present: {},
        dismiss: {},
        presentAnimationState: {},
        dismissAnimationState: {},
        presentClassName,
        dismissClassName,
        presentDuration,
        dismissDuration,
        insertPresentedContent,
        insertDismissedContent,
        removeContent,
        get presentCount() {
            return Object.keys(self.present).length;
        },
        get dismissCount() {
            return Object.keys(self.dismiss).length;
        }
    }));
    self.appear = appear;
    self.sequent = sequent;
    self.enqueue = enqueue;
    self.presentClassName = presentClassName;
    self.dismissClassName = dismissClassName;
    self.presentDuration = presentDuration;
    self.dismissDuration = dismissDuration;
    self.presentDelay = presentDelay;
    self.dismissDelay = dismissDelay;
    self.presentBeginHandler = presentBeginHandler;
    self.dismissBeginHandler = dismissBeginHandler;
    self.presentFinishHandler = presentFinishHandler;
    self.dismissFinishHandler = dismissFinishHandler;
    const [content, setContent] = useState({});
    /**
     * @function insertPresentedContent
     * @since 1.0.0
     * @hidden
     */
    function insertPresentedContent() {
        setContent((content) => (Object.assign(Object.assign({}, content), self.present)));
    }
    /**
     * @function insertDismissedContent
     * @since 1.0.0
     * @hidden
     */
    function insertDismissedContent() {
        setContent((content) => (Object.assign(Object.assign({}, content), self.dismiss)));
    }
    /**
     * @function removeContent
     * @since 1.0.0
     * @hidden
     */
    function removeContent(key) {
        setContent((content) => {
            delete content[key];
            return Object.assign({}, content);
        });
    }
    /**
     * @function render
     * @since 1.0.0
     * @hidden
     */
    function render() {
        return map(content, (child) => React.cloneElement(child, {
            ref: (ref) => watch(self, child.key, ref)
        }));
    }
    useEffect(() => {
        const current = self.content;
        const outcome = filter(keyed(children));
        const [insert, remove, update] = diff(current, outcome);
        setContent(Object.assign(Object.assign({}, content), update));
        const insertKeys = Object.keys(insert);
        const removeKeys = Object.keys(remove);
        if (insertKeys.length == 0 && removeKeys.length == 0) {
            return;
        }
        for (const key in remove)
            dismissKey(self, key, remove[key]);
        for (const key in insert)
            presentKey(self, key, insert[key]);
        start(self);
    }, [children]);
    useEffect(() => {
        if (self.onRender) {
            self.onRender();
            self.onRender = null;
        }
    });
    useEffect(() => {
        return () => stop(self);
    }, []);
    return render();
}
/**
 * @component TransitionItem
 * @since 1.0.0
 */
export class TransitionItem extends React.Component {
    /**
     * @inherited
     * @method render
     * @since 1.0.0
     */
    render() {
        return this.props.children;
    }
}
/**
 * @interface AnimationState
 * @since 1.0.0
 * @hidden
 */
var AnimationState;
(function (AnimationState) {
    AnimationState[AnimationState["Awaits"] = 0] = "Awaits";
    AnimationState[AnimationState["Active"] = 1] = "Active";
    AnimationState[AnimationState["Dispose"] = 2] = "Dispose";
})(AnimationState || (AnimationState = {}));
/**
 * @function presentKey
 * @since 1.0.0
 * @hidden
 */
function presentKey(self, key, node) {
    if (self.presentAnimationState[key] == null) {
        self.present[key] = node;
        self.content[key] = node;
    }
    delete self.dismiss[key];
}
/**
 * @function dismissKey
 * @since 1.0.0
 * @hidden
 */
function dismissKey(self, key, node) {
    if (self.dismissAnimationState[key]) {
        return;
    }
    if (self.present[key]) {
        delete self.present[key];
        delete self.content[key];
    }
    else {
        self.dismiss[key] = node;
    }
}
/**
 * @function start
 * @since 1.0.0
 * @hidden
 */
function start(self) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.enqueue && self.animate) {
            return;
        }
        if (self.presentCount == 0 && self.dismissCount == 0) {
            return;
        }
        if (self.empty && self.appear == false) {
            self.empty = false;
            yield startContent(self);
            return;
        }
        self.animate = true;
        if (self.sequent) {
            yield startSequence(self);
        }
        else {
            yield startTogether(self);
        }
        self.animate = false;
        start(self);
    });
}
/**
 * @function startContent
 * @since 1.0.0
 * @hidden
 */
function startContent(self) {
    return __awaiter(this, void 0, void 0, function* () {
        self.insertPresentedContent();
        // await waitForAnimationFrame(self);
        yield waitForRender(self);
        self.present = {};
        self.dismiss = {};
    });
}
/**
 * @function startSequence
 * @since 1.0.0
 * @hidden
 */
function startSequence(self) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.dismissCount) {
            self.insertDismissedContent();
            // await waitForAnimationFrame(self);
            yield waitForRender(self);
            yield dismissItems(self);
        }
        if (self.presentCount) {
            self.insertPresentedContent();
            //await waitForAnimationFrame(self);
            yield waitForRender(self);
            yield presentItems(self);
        }
    });
}
/**
 * @function startTogether
 * @since 1.0.0
 * @hidden
 */
function startTogether(self) {
    return __awaiter(this, void 0, void 0, function* () {
        self.insertPresentedContent();
        self.insertDismissedContent();
        // await waitForAnimationFrame(self);
        yield waitForRender(self);
        return Promise.all([dismissItems(self), presentItems(self)]);
    });
}
/**
 * @function dismissItems
 * @since 1.0.0
 * @hidden
 */
function dismissItems(self) {
    return __awaiter(this, void 0, void 0, function* () {
        return Promise.all(Object.keys(self.dismiss).map((key) => dismiss(self, key)));
    });
}
/**
 * @function presentItems
 * @since 1.0.0
 * @hidden
 */
function presentItems(self) {
    return __awaiter(this, void 0, void 0, function* () {
        return Promise.all(Object.keys(self.present).map((key) => present(self, key)));
    });
}
/**
 * @function present
 * @since 1.0.0
 * @hidden
 */
function present(self, key) {
    return __awaiter(this, void 0, void 0, function* () {
        const node = self.nodes[key];
        if (node == null) {
            throw new UnexpectedError();
        }
        delete self.present[key];
        yield waitForPresentToBegin(self, key, node);
        removeDismissClasses(self, key, node);
        appendPresentClasses(self, key, node);
        yield waitForPresentToFinish(self, key, node);
        removePresentClasses(self, key, node);
    });
}
/**
 * @function dismiss
 * @since 1.0.0
 * @hidden
 */
function dismiss(self, key) {
    return __awaiter(this, void 0, void 0, function* () {
        const node = self.nodes[key];
        if (node == null) {
            throw new UnexpectedError();
        }
        delete self.dismiss[key];
        yield waitForDismissToBegin(self, key, node);
        removePresentClasses(self, key, node);
        appendDismissClasses(self, key, node);
        yield waitForDismissToFinish(self, key, node);
        removeDismissClasses(self, key, node);
        appendDisposeClasses(self, key, node);
        delete self.content[key];
        self.removeContent(key);
    });
}
/**
 * @function waitForDismissToBegin
 * @since 1.0.0
 * @hidden
 */
function waitForDismissToBegin(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.dismissBeginHandler) {
            yield self.dismissBeginHandler(key, node);
        }
        else if (self.dismissDelay) {
            yield wait(self.dismissDelay);
        }
    });
}
/**
 * @function waitForDismissToFinish
 * @since 1.0.0
 * @hidden
 */
function waitForDismissToFinish(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.dismissFinishHandler) {
            yield self.dismissFinishHandler(key, node);
        }
        else if (self.dismissDuration) {
            yield wait(self.dismissDuration);
        }
        if (self.onAfterDismiss) {
            self.onAfterDismiss(node);
        }
    });
}
/**
 * @function waitForPresentToBegin
 * @since 1.0.0
 * @hidden
 */
function waitForPresentToBegin(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.presentBeginHandler) {
            yield self.presentBeginHandler(key, node);
        }
        else if (self.presentDelay) {
            yield wait(self.presentDelay);
        }
    });
}
/**
 * @function waitForPresentToFinish
 * @since 1.0.0
 * @hidden
 */
function waitForPresentToFinish(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.presentFinishHandler) {
            yield self.presentFinishHandler(key, node);
        }
        else if (self.presentDuration) {
            yield wait(self.presentDuration);
        }
        if (self.onAfterPresent) {
            self.onAfterPresent(node);
        }
    });
}
/**
 * @function waitForAnimationFrame
 * @since 1.0.0
 * @hidden
 */
function waitForAnimationFrame(self) {
    return __awaiter(this, void 0, void 0, function* () {
        if (self.frame) {
            cancelAnimationFrame(self.frame);
            self.frame = 0;
        }
        return new Promise((success) => {
            self.frame = requestAnimationFrame(success);
        });
    });
}
function waitForRender(self) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((success) => {
            self.onRender = () => success();
        });
    });
}
/**
 * @function appendPresentClasses
 * @since 1.0.0
 * @hidden
 */
function appendPresentClasses(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        yield appendClass(node, self.presentClassName);
        self.presentAnimationState[key] = AnimationState.Awaits;
        yield appendClass(node, self.presentClassName + '-active', true, self.onBeforePresent);
        self.presentAnimationState[key] = AnimationState.Active;
    });
}
/**
 * @function removePresentClasses
 * @since 1.0.0
 * @hidden
 */
function removePresentClasses(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        delete self.presentAnimationState[key];
        yield removeClass(node, self.presentClassName);
        yield removeClass(node, self.presentClassName + '-active');
    });
}
/**
 * @function appendDismissClasses
 * @since 1.0.0
 * @hidden
 */
function appendDismissClasses(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        yield appendClass(node, self.dismissClassName);
        self.dismissAnimationState[key] = AnimationState.Awaits;
        yield appendClass(node, self.dismissClassName + '-active', true, self.onBeforeDismiss);
        self.dismissAnimationState[key] = AnimationState.Active;
    });
}
/**
 * @function appendDisposeClasses
 * @since 1.0.0
 * @hidden
 */
function appendDisposeClasses(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        self.dismissAnimationState[key] = AnimationState.Dispose;
        yield appendClass(node, 'dispose');
    });
}
/**
 * @function removeDismissClasses
 * @since 1.0.0
 * @hidden
 */
function removeDismissClasses(self, key, node) {
    return __awaiter(this, void 0, void 0, function* () {
        delete self.dismissAnimationState[key];
        yield removeClass(node, self.dismissClassName);
        yield removeClass(node, self.dismissClassName + '-active');
    });
}
/**
 * @function stop
 * @since 1.0.0
 * @hidden
 */
function stop(self) {
    if (self.frame) {
        cancelAnimationFrame(self.frame);
        self.frame = 0;
    }
}
/**
 * @function watch
 * @since 1.0.0
 * @hidden
 */
function watch(self, key, ref) {
    var _a;
    if (ref == null) {
        (_a = self.watch[key]) === null || _a === void 0 ? void 0 : _a.disconnect();
        delete self.watch[key];
        delete self.nodes[key];
        return;
    }
    const node = getDOMNode(ref);
    if (node == null) {
        return null;
    }
    self.nodes[key] = node;
    if (self.watch[key] == null) {
        self.watch[key] = observer(self, key);
    }
    self.watch[key].observe(node, {
        attributes: true,
        attributeFilter: ['class'],
        characterData: false,
        childList: false
    });
}
/**
 * @function observer
 * @since 1.0.0
 * @hidden
 */
function observer(self, key) {
    /**
     * @function onMutate
     * @since 1.0.0
     * @hidden
     */
    function onMutate() {
        const node = self.nodes[key];
        if (node == null) {
            throw new UnexpectedError();
        }
        const presentState = self.presentAnimationState[key];
        const dismissState = self.dismissAnimationState[key];
        switch (presentState) {
            case AnimationState.Awaits:
                appendClass(node, self.presentClassName);
                break;
            case AnimationState.Active:
                appendClass(node, self.presentClassName + '-active');
                break;
        }
        switch (dismissState) {
            case AnimationState.Awaits:
                appendClass(node, self.dismissClassName);
                break;
            case AnimationState.Active:
                appendClass(node, self.dismissClassName + '-active');
                break;
            case AnimationState.Dispose:
                appendClass(node, 'dispose');
                break;
        }
    }
    return new MutationObserver(onMutate);
}
/**
 * @method filter
 * @since 1.0.0
 * @hidden
 */
function filter(content) {
    const output = {};
    for (const key in content) {
        const child = content[key];
        if (child == null) {
            continue;
        }
        const props = child.props;
        if (props) {
            if ('when' in props) {
                const condition = Boolean(props.when);
                if (condition == false) {
                    continue;
                }
            }
        }
        output[key] = child;
    }
    return output;
}
/**
 * @method appendClass
 * @since 1.0.0
 * @hidden
 */
export function appendClass(node, name, reflow = false, before) {
    if (node.classList.contains(name)) {
        return Promise.resolve();
    }
    if (reflow) {
        return new Promise((success) => {
            if (before) {
                before(node);
            }
            requestAnimationFrame(() => {
                node.scrollTop;
                node.classList.add(name);
                success();
            });
        });
    }
    if (before) {
        before(node);
    }
    node.classList.add(name);
    return Promise.resolve();
}
/**
 * @method removeClass
 * @since 1.0.0
 * @hidden
 */
export function removeClass(node, name, reflow = false, before) {
    if (node.classList.contains(name) == false) {
        return Promise.resolve();
    }
    if (reflow) {
        if (before) {
            before(node);
        }
        return new Promise((success) => {
            requestAnimationFrame(() => {
                node.scrollTop;
                node.classList.remove(name);
                success();
            });
        });
    }
    if (before) {
        before(node);
    }
    node.classList.remove(name);
    return Promise.resolve();
}
